import { Table, Tag } from 'react-dsgov';
import { IDeclaracaoTitularOuArrendatarioExistente } from '../../../../../interfaces/IDeclaracoes';
import { BrButton, BrLoading } from '@govbr-ds/react-components';
import { Link, useNavigate } from 'react-router-dom';
import styles from './existenteTables.module.scss';
import { mesParaAbreviacao } from '../../../../../utils/mesParaAbreviacao';
import { formatCurrency } from '../../../../../utils/formatCurrency';
import { formatarCNPJOrCpf } from '../../../../../utils/formatCNPJOrCPF';
import { useBackEndRequests } from '../../../lib/backEndRequests';
import { useNotificacao } from '../../../../../hooks/useNotificacao';

interface IHeader {
  field: string;
  label: string;
}

interface IProps {
  data: IDeclaracaoTitularOuArrendatarioExistente[];
  headers: IHeader[];
  isLoading: boolean;
  tab: string;
  page: 'titular-ou-arrendatario';
}

export const ExistentesTable = ({ data, headers, isLoading, tab, page }: IProps) => {
  const navigate = useNavigate();
  const mostrarNotificacao = useNotificacao();

  const { getRetificarDeclaracao } = useBackEndRequests();

  const editarOperacao = (id: string, mes: number) => {
    sessionStorage.setItem('mesDeclaracao', mes.toString());
    navigate(`/${page}/edit?id=${id}`);
  };

  const retificarDeclaracao = async (id: string, mes: number) => {
    sessionStorage.setItem('mesDeclaracao', mes.toString());
    await getRetificarDeclaracao(id)
      .then(async (reficadoraId) => {
        navigate(`/titular-ou-arrendatario/edit?id=${reficadoraId}`);
      })
      .catch((err) => {
        mostrarNotificacao({
          tipo: 'danger',
          conteudo: 'Erro ao retificar a declaração',
          ...(err && { complementoSentryId: err }),
        });
      });
  };

  const actionsColumn = (id: string, field: string, mes: number) => {
    if (field === 'acao-editar') {
      return (
        <BrButton onClick={() => editarOperacao(id, mes)}>
          <i className={`fas fa-edit fa-md ${styles.columnIcon}`} /> Continuar
        </BrButton>
      );
    } else if (field === 'acao-visualizar') {
      return (
        <>
          <BrButton onClick={() => retificarDeclaracao(id, mes)}>
            <i className={`fas fa-md ${styles.buttonIcon}`}>&#xf2f1;</i>
            Retificar
          </BrButton>{' '}
          <BrButton onClick={() => navigate(`/${page}/view?id=${id}`)}>
            <i className={`fas fa-eye fa-md ${styles.buttonIcon}`} />
            Visualizar
          </BrButton>
        </>
      );
    }
  };

  const statusColumn = (status: number, tab: string) => {
    if (tab === 'Entregue') {
      if (status === 1 || status === 3)
        return (
          <>
            <i className={`fas fa-check-circle fa-md ${styles.greenIcon}`} /> Entregue no prazo
          </>
        );
      if (status === 2 || status === 4)
        return (
          <>
            <i className={`fas fa-times-circle fa-md ${styles.redIcon}`} />
            Entregue fora do prazo
          </>
        );
    }
  };

  return (
    <div className={styles.customTable}>
      <Table showTopBar={false} headers={headers} showPagination={false}>
        <tbody>
          {data.length === 0 && !isLoading ? (
            <div className={styles.emptyText}>Não há declarações para serem exibidas</div>
          ) : (
            Array.isArray(data) &&
            data.map((row, index) => (
              <tr key={index}>
                {headers.map((header) => {
                  if (header?.label === 'Status')
                    return (
                      <td key={header.field} className={styles.statusColumn}>
                        {statusColumn(row['statusDeclaracao'], tab)}
                      </td>
                    );
                  else if (header?.label === 'Ação')
                    return (
                      <td key={header.field} className={styles.actionColumn}>
                        {actionsColumn(row.id, header.field, row['exercicioMes'])}
                      </td>
                    );
                  else if (header?.field === 'declarante') {
                    return (
                      <td key={header.field}>
                        {row.declarante?.cpfCnpj ? `${formatarCNPJOrCpf(row.declarante.cpfCnpj)}` : '-'}
                      </td>
                    );
                  } else if (header?.field === 'exercicio') {
                    return (
                      <td key={header.field}>{`${mesParaAbreviacao(row['exercicioMes'])} ${row['exercicioAno']}`}</td>
                    );
                  } else if (header?.field === 'tipoDeclaracao') {
                    return (
                      <td key={header.field}>
                        {row[header.field] === 1 ? (
                          <Tag type='text' className={styles.labelRetificadora} label='Retificadora' />
                        ) : (
                          <Tag type='text' className={styles.labelOriginal} label='Original' />
                        )}
                      </td>
                    );
                  } else if (header?.field === 'baseDeCalculo') {
                    return <td key={header.field}>{formatCurrency(row[header.field] || '')}</td>;
                  } else {
                    return <td key={header.field}>{(row as any)[header.field] || '-'}</td>;
                  }
                })}
              </tr>
            ))
          )}
        </tbody>
      </Table>
      {isLoading && (
        <div className={styles.tableLoading}>
          <BrLoading large label='Carregando...' />
        </div>
      )}
    </div>
  );
};
